import React from 'react';
import { MARKETS } from '../../store/useMarketStore';

interface PropertyBasicsProps {
  address: string;
  marketId: string;
  units: number | '';
  sqft: number | '';
  yearBuilt: number | '';
  errors: Record<string, string>;
  onChange: (field: string, value: string | number) => void;
}

export const PropertyBasicsForm: React.FC<PropertyBasicsProps> = ({
  address,
  marketId,
  units,
  sqft,
  yearBuilt,
  errors,
  onChange,
}) => {
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-stone-400 uppercase tracking-wider">
        Property Basics
      </h3>

      {/* Market */}
      <div>
        <label className="block text-sm font-medium text-stone-300 mb-1">Market</label>
        <select
          value={marketId}
          onChange={(e) => onChange('marketId', e.target.value)}
          className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-ivory focus:border-gold focus:outline-none"
        >
          {MARKETS.map((m) => (
            <option key={m.market_id} value={m.market_id}>
              {m.display_name}, {m.state}
            </option>
          ))}
        </select>
      </div>

      {/* Address */}
      <div>
        <label className="block text-sm font-medium text-stone-300 mb-1">Address</label>
        <input
          type="text"
          value={address}
          onChange={(e) => onChange('address', e.target.value)}
          placeholder="123 Main St, Norfolk, VA 23510"
          className={`w-full bg-slate-800 border rounded-lg px-3 py-2.5 text-sm text-ivory placeholder-stone-500 focus:outline-none ${
            errors.address ? 'border-red-500 focus:border-red-400' : 'border-slate-600 focus:border-gold'
          }`}
        />
        {errors.address && <p className="text-xs text-red-400 mt-1">{errors.address}</p>}
      </div>

      {/* Units */}
      <div>
        <label className="block text-sm font-medium text-stone-300 mb-1">Total Units</label>
        <input
          type="number"
          value={units}
          onChange={(e) => onChange('units', e.target.value ? Number(e.target.value) : '')}
          placeholder="4"
          min={1}
          className={`w-full bg-slate-800 border rounded-lg px-3 py-2.5 text-sm text-ivory placeholder-stone-500 focus:outline-none ${
            errors.units ? 'border-red-500 focus:border-red-400' : 'border-slate-600 focus:border-gold'
          }`}
        />
        {errors.units && <p className="text-xs text-red-400 mt-1">{errors.units}</p>}
      </div>

      {/* Square footage */}
      <div>
        <label className="block text-sm font-medium text-stone-300 mb-1">
          Square Footage <span className="text-stone-500">(optional)</span>
        </label>
        <input
          type="number"
          value={sqft}
          onChange={(e) => onChange('sqft', e.target.value ? Number(e.target.value) : '')}
          placeholder="3200"
          min={0}
          className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-ivory placeholder-stone-500 focus:border-gold focus:outline-none"
        />
      </div>

      {/* Year built */}
      <div>
        <label className="block text-sm font-medium text-stone-300 mb-1">
          Year Built <span className="text-stone-500">(optional)</span>
        </label>
        <input
          type="number"
          value={yearBuilt}
          onChange={(e) => onChange('yearBuilt', e.target.value ? Number(e.target.value) : '')}
          placeholder="1925"
          min={1800}
          max={2026}
          className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-ivory placeholder-stone-500 focus:border-gold focus:outline-none"
        />
      </div>
    </div>
  );
};
